import React, { useEffect } from 'react';
import FocusedDialog from './FocusedDialog';
import { cn } from '../../utils/cn';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Max-width preset or custom class. Defaults to 'max-w-2xl'. */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full' | string;
  /** Accessible label — either an `aria-label` string or a rendered title element. */
  ariaLabel?: string;
  /** ARIA role. Defaults to `dialog`. */
  role?: 'dialog' | 'alertdialog';
  /** When true, Escape key closes the modal. Defaults to true. */
  closeOnEscape?: boolean;
  /** When true, clicking the backdrop closes the modal. Defaults to true. */
  closeOnBackdrop?: boolean;
  /** Additional classes for the dialog shell. */
  className?: string;
  /** Everything inside the dialog (header, body, footer — caller's choice). */
  children: React.ReactNode;
}

const MAX_WIDTH_PRESETS: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-full',
};

/**
 * Shared modal shell — backdrop + centered dialog + Escape/backdrop-to-close.
 *
 * Replaces the hand-rolled `<div className="fixed inset-0 ..."> + <div className="absolute inset-0 bg-black/50">`
 * pattern that was duplicated across every schedule / basket / strategy modal.
 *
 * Keep header/body/footer inside `children` — this primitive is layout-agnostic
 * on purpose so callers can pick table / grid / card structure.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen, onClose,
  maxWidth = '2xl',
  ariaLabel,
  role = 'dialog',
  closeOnEscape = true,
  closeOnBackdrop = true,
  className,
  children,
}) => {
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const widthClass = MAX_WIDTH_PRESETS[maxWidth] || maxWidth;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden
      />
      <FocusedDialog
        role={role}
        ariaLabel={ariaLabel}
        className={cn(
          'relative w-full max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col',
          'animate-in fade-in zoom-in-95 duration-150',
          widthClass,
          className,
        )}
      >
        {children}
      </FocusedDialog>
    </div>
  );
};

export default Modal;